'use client';

import { useEffect, useState } from 'react';
import { useHospitalitySource } from './hospitalitySource';
import type { RestaurantSite } from './wire';

/**
 * The restaurant this page belongs to, read through the host data source.
 *
 * <p>Every restaurant block starts here, and none of them calls `fetch`: the
 * source is the seam, and it is the host — not the block — that knows which
 * hostname the visitor typed. That is what lets the same `OpeningHours` block
 * sit on any tenant's page.
 *
 * <p>Five states, kept distinct because they mean different things to whoever
 * has to fix them. `no-source` is a host that has not mounted the provider —
 * a normal state on a non-restaurant tenant, and not an error. `missing` is a
 * hostname no channel claims, which is the operator's DNS or `Channel.DOMAIN`
 * to fix. `error` is the backend being unreachable, which nobody should see
 * rendered as "this restaurant does not exist".
 */
export type RestaurantSiteState =
  | { status: 'loading' }
  | { status: 'no-source' }
  | { status: 'missing' }
  | { status: 'error'; error: unknown }
  | { status: 'ready'; site: RestaurantSite };

export function useRestaurantSite(): RestaurantSiteState {
  const source = useHospitalitySource();
  const [state, setState] = useState<RestaurantSiteState>(
    source ? { status: 'loading' } : { status: 'no-source' },
  );

  useEffect(() => {
    if (!source) {
      setState({ status: 'no-source' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    source
      .site()
      .then((site) => {
        if (cancelled) return;
        setState(site ? { status: 'ready', site } : { status: 'missing' });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({ status: 'error', error });
      });
    return () => {
      cancelled = true;
    };
  }, [source]);

  return state;
}
